// src/shared/lib/errors/i18n.ts

type TFunc = (key: string, opts?: Record<string, unknown>) => string;

interface AnyErrorShape {
  message?: string;
  response?: {
    status?: number;
    data?: { code?: string; message?: string; details?: Record<string, unknown> };
  };
}

/**
 * Convert any backend error into a vendor-facing translated string.
 *
 * Lookup order:
 *   1. err.response.data.code → `errors.<code>` translation
 *   2. Network / status-0 / "Network Error" message → `errors.network`
 *   3. err.response.data.message (BE-provided English) — last-resort, never
 *      a blank toast
 *   4. `errors.generic`
 *
 * The codemod populates `errors.<code>` keys as it discovers new codes in
 * existing source. Unknown codes emit a dev-only console warning so the
 * team catches drift.
 */
export function translateBackendError(err: unknown, t: TFunc): string {
  const e = (err as AnyErrorShape) ?? {};

  const code = e.response?.data?.code;
  if (code) {
    const translationKey = `errors.${code}`;
    // Pass the backend's `details` payload through as interpolation options —
    // e.g. INSUFFICIENT_STOCK's `{ available, requested }` — so a translation
    // that references `{{available}}` renders the real count. Harmless for a
    // translation with no placeholders.
    const translated = t(translationKey, e.response?.data?.details);
    // i18next returns the key itself when missing — we treat that as "no translation"
    if (translated !== translationKey) return translated;
    // Dev-only warning for missing translation keys
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[i18n] No translation for backend error code "${code}" — add errors.${code} to src/locales/en/errors.json.`,
      );
    }
  }

  const msg = e.message ?? e.response?.data?.message;
  if (typeof msg === "string" && /network/i.test(msg)) {
    return t("errors.network");
  }
  if (e.response?.status === undefined && msg) {
    // No HTTP status = pure network failure
    return t("errors.network");
  }

  if (
    typeof e.response?.data?.message === "string" &&
    e.response.data.message.length > 0
  ) {
    return e.response.data.message;
  }

  return t("errors.generic");
}
